import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, MessageCircle, Clock } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { buildWhatsAppUrl, openWhatsApp } from "../lib/whatsapp";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";

const Contact = () => {
  const { content } = useContent();
  const c = content.company || {};
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast({ title: "Eksik bilgi", description: "Lütfen zorunlu alanları doldurun." });
      return;
    }
    setSending(true);
    setTimeout(() => {
      setSending(false);
      toast({ title: "Mesajınız alındı!", description: "En kısa sürede sizinle iletişime geçeceğiz." });
      setForm({ name: "", phone: "", subject: "", message: "" });
    }, 900);
  };

  const contacts = [
    { icon: Phone, label: "Telefon", value: c.phone, link: `tel:${(c.phone || "").replace(/\s/g, "")}` },
    { icon: Mail, label: "E-posta", value: c.email, link: `mailto:${c.email || ""}` },
    { icon: MapPin, label: "Adres", value: c.address, link: "#" },
    { icon: Clock, label: "Çalışma Saatleri", value: "Pzt - Cmt: 08:00 - 19:00", link: "#" }
  ];

  const waMsg = "Merhaba, Aktav Yarka hakkında bilgi ve fiyat teklifi almak istiyorum.";

  return (
    <section id="contact" className="py-24 bg-[#b91c1c] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 20px 20px, white 1.5px, transparent 0)", backgroundSize: "40px 40px" }} />
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="inline-block text-xs font-semibold tracking-[0.25em] text-white/70 uppercase mb-3">— İletişim</span>
          <h2 className="text-3xl md:text-4xl font-black text-white">Bize Ulaşın</h2>
          <p className="text-white/75 mt-3">Sorularınız ve fiyat teklifi için formu doldurun ya da WhatsApp üzerinden anında iletişime geçin.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {contacts.map((ct, i) => (
              <a key={i} href={ct.link} className="flex items-center gap-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-5 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-white text-[#b91c1c] flex items-center justify-center shrink-0"><ct.icon className="w-5 h-5" /></div>
                <div>
                  <div className="text-white/60 text-xs uppercase tracking-wider mb-0.5">{ct.label}</div>
                  <div className="text-white font-semibold text-sm">{ct.value}</div>
                </div>
              </a>
            ))}
            <a href={buildWhatsAppUrl(c.whatsapp, waMsg)} onClick={(e) => { e.preventDefault(); openWhatsApp(c.whatsapp, waMsg); }} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-4 rounded-xl transition-colors">
              <MessageCircle className="w-5 h-5" /> WhatsApp ile Hemen Yaz
            </a>
          </div>

          <form onSubmit={onSubmit} className="lg:col-span-3 bg-white rounded-2xl p-7 md:p-9 shadow-2xl">
            <h3 className="text-xl font-bold text-[#b91c1c] mb-1">Teklif Formu</h3>
            <p className="text-sm text-slate-500 mb-6">Doldurun, sizi arayalım.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1.5 block">İsim Soyisim *</label>
                <Input name="name" value={form.name} onChange={onChange} placeholder="Adınız" className="h-11" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Telefon *</label>
                <Input name="phone" value={form.phone} onChange={onChange} placeholder="0540 688 19 99" className="h-11" />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Konu</label>
              <Input name="subject" value={form.subject} onChange={onChange} placeholder="örn. Lohman Brown 100 adet fiyatı" className="h-11" />
            </div>
            <div className="mt-4">
              <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Mesajınız *</label>
              <Textarea name="message" value={form.message} onChange={onChange} placeholder="Talebinizi yazın..." rows={5} />
            </div>
            <Button type="submit" disabled={sending} size="lg" className="mt-6 bg-[#b91c1c] hover:bg-[#991b1b] text-white w-full sm:w-auto px-8 h-12 font-semibold">
              {sending ? "Gönderiliyor..." : (<><Send className="w-4 h-4 mr-2" /> Mesajı Gönder</>)}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
